import React, { useMemo } from "react";

import { useChartData, UseChartDataParams } from "@/api/useChartData";

import { ScatterChartCard, ScatterDataset } from ".";

/**
 * Union type for scatter chart types that contain coordinate point data.
 * Used to ensure type safety when accessing specific coordinate fields.
 */
type ScatterChartType = "release_points" | "break_and_movement";

/**
 * Subset of ChartsData type containing only the fields relevant for scatter charts.
 * Properly types the coordinate arrays to avoid casting and improve type safety.
 */
type ChartDataset = {
  pitch_type?: string | null;
  points_release?: number[][] | null;
  points_break_and_movement?: number[][] | null;
};

/**
 * Props for the ScatterChartDataCard component.
 *
 * This interface extends the base chart data parameters to provide
 * scatter chart specific configuration.
 */
export interface ScatterChartDataCardProps extends UseChartDataParams {
  /** The title to display on the scatter chart card */
  title: string;
  /** Label for the horizontal axis */
  horizontalLabel?: string;
  /** Label for the vertical axis */
  verticalLabel?: string;
  /** Whether to show the legend - 'auto' shows legend if multiple datasets */
  showLegend?: true | false | "auto";
  /** Whether to show the origin axis lines */
  showOriginAxis?: boolean;
  /** Unit of measurement for the chart */
  unit?: string;
  /** Filter by specific result type (optional) */
  resultType?: "out" | "hit" | "whiff" | "contact";
  /** Filter by specific pitch type (optional) */
  pitchType?: string;
}

export const PitchTypeColors: Record<string, string> = {
  Fastball: "#FF0508",
  Splitter: "#032BFB",
  Curveball: "#084B12",
  Slider: "#E27528",
  Cutter: "#2DF206",
  Changeup: "#7E7E7E",
  Other: "#000000",
};

/**
 * ScatterChartDataCard Component
 *
 * A data-driven scatter chart visualization component that fetches real player data
 * and displays it as a scatter plot for baseball analytics.
 *
 * Features:
 * - Fetches live player chart data from the API
 * - Transforms backend data into scatter plot format
 * - Shows loading states and error handling
 * - Supports various filters (result type, pitch type, etc.)
 *
 * @param props - Configuration for the scatter chart including data parameters and display options
 * @returns A rendered scatter chart card with player data
 *
 * @example
 * ```tsx
 * <ScatterChartDataCard
 *   title="Release Point"
 *   playerExternalId="player-123"
 *   startDate="2024-01-01"
 *   endDate="2024-12-31"
 *   gameType="game"
 *   playerType="pitcher"
 *   vsHandedness="all"
 *   chartType="scatter"
 *   horizontalLabel="Horizontal Break"
 *   verticalLabel="Induced Vertical Break"
 *   unit="in"
 * />
 * ```
 */
export const ScatterChartDataCard: React.FC<ScatterChartDataCardProps> = ({
  title,
  horizontalLabel,
  verticalLabel,
  showLegend = "auto",
  showOriginAxis = true,
  unit = "in",
  playerExternalId,
  startDate,
  endDate,
  gameType,
  playerType,
  vsHandedness,
  chartType,
  resultType,
  pitchType,
  filters,
  groupBy,
}) => {
  const { data, isLoading, error } = useChartData({
    playerExternalId,
    startDate,
    endDate,
    gameType,
    playerType,
    vsHandedness,
    chartType,
    resultType,
    pitchType,
    filters,
    groupBy,
  });

  const datasets: ScatterDataset[] = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    // Map scatter chart types to their corresponding field names
    const scatterChartFields: Record<ScatterChartType, keyof ChartDataset> = {
      release_points: "points_release",
      break_and_movement: "points_break_and_movement",
    };

    // Transform the data into the format expected by ScatterChartCard
    return data.map((rawDataset) => {
      const dataset = rawDataset as ChartDataset;
      let points: { x: number; y: number }[] = [];
      const fieldName = scatterChartFields[chartType as ScatterChartType];

      if (fieldName) {
        // Extract and transform the coordinates - properly typed!
        const coordinates = dataset[fieldName];
        if (coordinates && Array.isArray(coordinates)) {
          points = coordinates.map((coords: number[]) => ({
            x: coords[0],
            y: coords[1],
          }));
        }
      }

      return {
        name: dataset.pitch_type || "Other",
        color:
          PitchTypeColors[dataset.pitch_type || "Other"] ||
          PitchTypeColors.Other,
        points,
      };
    });
  }, [data, chartType]);

  if (error) {
    return (
      <div className="w-full min-h-[330px] h-full flex items-center justify-center bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-500">Unable to load chart data</p>
      </div>
    );
  }

  return (
    <ScatterChartCard
      title={title}
      horizontalLabel={horizontalLabel}
      verticalLabel={verticalLabel}
      showLegend={showLegend}
      showOriginAxis={showOriginAxis}
      unit={unit}
      datasets={datasets}
      loading={isLoading}
    />
  );
};
